import React, {
  useEffect,
  useRef,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { useDropzone, FileRejection, Accept } from 'react-dropzone';
import * as lib from '~/dependencies';
import settings from '~/assets/settings.json';
import FileListModal from './FileListModal';
import {
  ThumbsContainer,
  Thumb,
  ThumbInner,
  ThumbImage,
  NonImagePreview,
  NonImageBadge,
  NonImageName,
  RemoveImageButton,
  ThumbMoreOverlay,
} from './styles';

const MAX_VISIBLE_THUMBS = settings.conversations.maxVisibleFileThumbnails;

interface FileCustom extends File {
  preview?: string;
}

interface FileSizeLimits {
  image: number;
  other: number;
}

interface FilePickerPreviewProps {
  children?: ReactNode;
  /** Disables the dropzone while still rendering `children`. */
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  files: File[];
  maxFiles: number;
  maxFileSize: FileSizeLimits;
  acceptTypes: Accept;
  allowedFileExtensions: string[];
  onDropAccepted: (file: File[] | undefined) => void;
  onRemove?: (file: File) => void;
  onThumbClick?: (file: FileCustom) => void;
}

function isImage(file: FileCustom): boolean {
  return file.type.startsWith('image/');
}

function getExtension(fileName: string): string {
  const segments = fileName.split('.');
  if (segments.length < 2) {
    return 'FILE';
  }

  return segments[segments.length - 1].toUpperCase();
}

function FilePickerPreview(props: FilePickerPreviewProps) {
  const {
    children,
    disabled,
    files,
    inputRef,
    maxFiles,
    maxFileSize,
    acceptTypes,
    allowedFileExtensions,
    onDropAccepted,
    onRemove,
    onThumbClick,
  } = props;

  const [isFileListOpen, setIsFileListOpen] = useState(false);

  const resetInput = useCallback(() => {
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  }, [inputRef]);

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const firstRejection = fileRejections[0];
      const rejectionCode = firstRejection?.errors[0]?.code;
      const fileName = firstRejection?.file?.name;

      const rejectionMessages: Record<
        string,
        { message: string; type: 'info' | 'warn' | 'error' }
      > = {
        'file-too-large': {
          message:
            'File size exceeds the maximum allowed limit for attachments.',
          type: 'error',
        },
        'file-invalid-type': {
          message: 'File type is invalid for attachments.',
          type: 'error',
        },
        'file-extension-not-allowed': {
          message: 'File type is invalid for attachments.',
          type: 'error',
        },
        'too-many-files': {
          message: `Upload limit reached for attachments. ${maxFiles} file(s) allowed.`,
          type: 'info',
        },
      };

      const rejection = rejectionCode
        ? rejectionMessages[rejectionCode]
        : undefined;

      lib.ToastMessage(
        rejection?.type || 'error',
        rejection?.message ||
          `The file ${fileName || 'selected'} is not a supported format`
      );
      resetInput();
    },
    [resetInput, maxFiles]
  );

  // const onDrop = useCallback(
  //   (acceptedFiles: File[]) => {
  //     onDropAccepted(
  //       acceptedFiles.map(
  //         (file: File) =>
  //           Object.assign(file, {
  //             preview: file.type.startsWith('image/')
  //               ? URL.createObjectURL(file)
  //               : undefined,
  //           }) as FileCustom
  //       )
  //     );
  //   },
  //   [onDropAccepted]
  // );

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptTypes,
    disabled,
    noClick: true,
    noKeyboard: true,
    maxFiles,
    validator: file => {
      // FIXME: Read more on WA2-7392
      const extension = file.name
        .split('.')
        .pop()
        ?.toLowerCase();

      if (extension && !allowedFileExtensions.includes(extension)) {
        return {
          code: 'file-extension-not-allowed',
          message: 'File extension is not allowed',
        };
      }

      const isImageFile = file.type.startsWith('image/');

      const limitSize = isImageFile ? maxFileSize.image : maxFileSize.other;

      if (file.size > limitSize * 1000 * 1000) {
        return {
          code: 'file-too-large',
          message: `${
            isImageFile ? 'Image' : 'File'
          } exceeds the ${limitSize}MB limit`,
        };
      }
      return null;
    },
    onDropRejected,
    // onDrop,
    onDropAccepted: acceptedFiles => {
      const remainingSlots = maxFiles - filesRef.current.length;

      if (remainingSlots <= 0) {
        lib.ToastMessage(
          'info',
          `Upload limit reached for attachments. ${maxFiles} file(s) allowed.`
        );
        resetInput();
        return;
      }

      const filesToAdd = acceptedFiles.slice(0, remainingSlots);

      if (acceptedFiles.length > remainingSlots) {
        lib.ToastMessage(
          'info',
          `Upload limit reached for attachments. ${maxFiles} file(s) allowed.`
        );
      }

      if (onDropAccepted && filesToAdd.length > 0) {
        onDropAccepted(
          filesToAdd.map(
            (file: File) =>
              Object.assign(file, {
                preview: file.type.startsWith('image/')
                  ? URL.createObjectURL(file)
                  : undefined,
              }) as FileCustom
          )
        );
      }
    },
  });

  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    return () => {
      filesRef.current.forEach((file: FileCustom) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  function removeFile(file: File): void {
    const f = file as FileCustom;
    if (f.preview) URL.revokeObjectURL(f.preview);
    onRemove?.(file);

    const remaining = files.filter(existing => existing !== file);
    if (remaining.length === 0) {
      resetInput();
      setIsFileListOpen(false);
    }
  }

  const visibleFiles = files.slice(0, MAX_VISIBLE_THUMBS);
  const remainingCount = files.length - MAX_VISIBLE_THUMBS;

  const thumbs = visibleFiles.map((file: FileCustom, index: number) => {
    const isLastVisible = index === visibleFiles.length - 1;
    const isOverflowTile = isLastVisible && remainingCount > 0;

    return (
      <Thumb
        key={file.name}
        onClick={() =>
          isOverflowTile ? setIsFileListOpen(true) : onThumbClick?.(file)
        }
        style={{ cursor: 'pointer' }}>
        {!isOverflowTile && (
          <RemoveImageButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              removeFile(file);
            }}
            title={`Remove ${file.name}`}>
            ×
          </RemoveImageButton>
        )}
        <ThumbInner>
          {isImage(file) && file.preview ? (
            <ThumbImage src={file.preview} title={file.name} alt={file.name} />
          ) : (
            <NonImagePreview title={file.name}>
              <NonImageBadge>{getExtension(file.name)}</NonImageBadge>
              <NonImageName>{file.name}</NonImageName>
            </NonImagePreview>
          )}
          {isOverflowTile && (
            <ThumbMoreOverlay>+{remainingCount}</ThumbMoreOverlay>
          )}
        </ThumbInner>
      </Thumb>
    );
  });

  return (
    <section
      className="file-picker-preview-container"
      style={{ width: '100%' }}>
      {thumbs.length > 0 && <ThumbsContainer>{thumbs}</ThumbsContainer>}
      <div
        {...getRootProps({
          className: 'dropzone',
        })}>
        <input
          {...getInputProps()}
          ref={inputRef}
          style={{ display: 'none' }}
          tabIndex={-1}
        />
        {children}
      </div>
      {isFileListOpen && (
        <FileListModal
          files={files}
          open={isFileListOpen}
          onClose={() => setIsFileListOpen(false)}
          onRemove={removeFile}
        />
      )}
    </section>
  );
}

FilePickerPreview.defaultProps = {
  children: null,
  disabled: false,
  inputRef: null,
  onRemove: undefined,
  onThumbClick: () => false,
};

export default FilePickerPreview;
